// AGC: Activity Logger Service
// AGC: Creates activity log entries in the notifications system
// AGC: Activities are user actions (creating entries) - they appear in the log but NOT in bell badge

import { createNotification } from './notifications'
import type { Notification, NotificationType, NotificationPriority, NotificationAction } from '../types'

/**
 * AGC: Activity type configuration
 * AGC: Defines icons and titles for different activity types
 */
export interface ActivityConfig {
  type: NotificationType
  action: NotificationAction
  title: string
  description?: string
  icon: string
  priority?: NotificationPriority
  relatedId?: string
  metadata?: Notification['metadata']
}

/**
 * AGC: Pre-configured activity types for common actions
 */
export const ACTIVITY_CONFIGS = {
  // AGC: Field/Plot activities
  FIELD_CREATED: (name: string, area?: number): ActivityConfig => ({
    type: 'field',
    action: 'created',
    title: `Talhão criado: ${name}`,
    description: area ? `Área: ${area} hectares` : undefined,
    icon: '🗺️',
    priority: 'low',
  }),
  
  FIELD_UPDATED: (name: string): ActivityConfig => ({
    type: 'field',
    action: 'updated',
    title: `Talhão atualizado: ${name}`,
    icon: '🗺️',
    priority: 'low',
  }),
  
  FIELD_DELETED: (name: string): ActivityConfig => ({
    type: 'field',
    action: 'deleted',
    title: `Talhão removido: ${name}`,
    icon: '🗺️',
    priority: 'low',
  }),
  
  // AGC: Task activities
  TASK_CREATED: (title: string, category?: string): ActivityConfig => ({
    type: 'task',
    action: 'created',
    title: `Tarefa criada: ${title}`,
    description: category ? `Categoria: ${category}` : undefined,
    icon: '✅',
    priority: 'low',
  }),
  
  TASK_COMPLETED: (title: string): ActivityConfig => ({
    type: 'task',
    action: 'completed',
    title: `Tarefa concluída: ${title}`,
    icon: '✅',
    priority: 'low',
  }),
  
  TASK_UPDATED: (title: string): ActivityConfig => ({
    type: 'task',
    action: 'updated',
    title: `Tarefa atualizada: ${title}`,
    icon: '✅',
    priority: 'low',
  }),
  
  // AGC: Financial activities
  EXPENSE_CREATED: (title: string, amount: number): ActivityConfig => ({
    type: 'financial',
    action: 'created',
    title: `Despesa registrada: ${title}`,
    description: `R$ ${amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
    icon: '💰',
    priority: 'low',
    metadata: {
      amount,
      category: 'expense',
    },
  }),
  
  INCOME_CREATED: (title: string, amount: number): ActivityConfig => ({
    type: 'financial',
    action: 'created',
    title: `Receita registrada: ${title}`,
    description: `R$ ${amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
    icon: '💵',
    priority: 'low',
    metadata: {
      amount,
      category: 'income',
    },
  }),
  
  // AGC: Inventory activities
  INVENTORY_ADDED: (item: string, quantity?: number): ActivityConfig => ({
    type: 'inventory',
    action: 'created',
    title: `Item adicionado: ${item}`,
    description: quantity ? `Quantidade: ${quantity}` : undefined,
    icon: '📦',
    priority: 'low',
  }),
  
  // AGC: Equipment activities
  EQUIPMENT_ADDED: (name: string): ActivityConfig => ({
    type: 'equipment',
    action: 'created',
    title: `Equipamento cadastrado: ${name}`,
    icon: '🚜',
    priority: 'low',
  }),
  
  // AGC: Crop activities
  CROP_PLANTED: (cropType: string, fieldName?: string): ActivityConfig => ({
    type: 'crop',
    action: 'created',
    title: `Plantio registrado: ${cropType}`,
    description: fieldName ? `Talhão: ${fieldName}` : undefined,
    icon: '🌱',
    priority: 'low',
  }),
  
  // AGC: Worker activities
  WORKER_ADDED: (name: string): ActivityConfig => ({
    type: 'worker',
    action: 'created',
    title: `Trabalhador cadastrado: ${name}`,
    icon: '👷',
    priority: 'low',
  }),
}

/**
 * AGC: Log a user activity
 * AGC: Creates a notification with isActivity=true (won't show in bell badge)
 * 
 * @param config - Activity configuration
 * @param source - How the activity was triggered ('click' | 'voice' | 'system')
 * @param authToken - User's auth token
 * @param relatedIds - Optional related entity IDs
 */
export const logActivity = async (
  config: ActivityConfig,
  source: 'click' | 'voice' | 'system',
  authToken?: string | null,
  relatedIds?: {
    fieldId?: string
    plotId?: string
    taskId?: string
    transactionId?: string
    equipmentId?: string
    workerId?: string
  }
): Promise<Notification | null> => {
  try {
    const notification: Partial<Notification> = {
      type: config.type,
      action: config.action,
      title: config.title,
      description: config.description,
      icon: config.icon,
      priority: config.priority || 'low',
      timestamp: new Date().toISOString(),
      read: true, // AGC: Activities start as read (no badge)
      isActivity: true, // AGC: CRITICAL - Marks as activity, not notification
      ...relatedIds,
      metadata: {
        ...config.metadata,
        source,
      },
    }
    
    const created = await createNotification(notification, authToken)
    console.log(`📝 AGC: Activity logged: ${config.title} (source: ${source})`)
    return created
  } catch (error) {
    console.error('AGC: Error logging activity:', error)
    // AGC: Don't throw - activity logging should not break the main flow
    return null
  }
}

/**
 * AGC: Quick helper to log field activity
 */
export const logFieldActivity = async (
  action: 'created' | 'updated' | 'deleted',
  fieldName: string,
  source: 'click' | 'voice',
  authToken?: string | null,
  fieldId?: string,
  area?: number
): Promise<Notification | null> => {
  const configs = {
    created: ACTIVITY_CONFIGS.FIELD_CREATED(fieldName, area),
    updated: ACTIVITY_CONFIGS.FIELD_UPDATED(fieldName),
    deleted: ACTIVITY_CONFIGS.FIELD_DELETED(fieldName),
  }
  return logActivity(configs[action], source, authToken, { fieldId })
}

/**
 * AGC: Quick helper to log task activity
 */
export const logTaskActivity = async (
  action: 'created' | 'completed' | 'updated',
  taskTitle: string,
  source: 'click' | 'voice',
  authToken?: string | null,
  taskId?: string,
  category?: string
): Promise<Notification | null> => {
  const configs = {
    created: ACTIVITY_CONFIGS.TASK_CREATED(taskTitle, category),
    completed: ACTIVITY_CONFIGS.TASK_COMPLETED(taskTitle),
    updated: ACTIVITY_CONFIGS.TASK_UPDATED(taskTitle),
  }
  return logActivity(configs[action], source, authToken, { taskId })
}

/**
 * AGC: Quick helper to log expense activity
 */
export const logExpenseActivity = async (
  title: string,
  amount: number,
  source: 'click' | 'voice',
  authToken?: string | null,
  transactionId?: string
): Promise<Notification | null> => {
  return logActivity(
    ACTIVITY_CONFIGS.EXPENSE_CREATED(title, amount),
    source,
    authToken,
    { transactionId }
  )
}

/**
 * AGC: Quick helper to log income activity
 */
export const logIncomeActivity = async (
  title: string,
  amount: number,
  source: 'click' | 'voice',
  authToken?: string | null,
  transactionId?: string
): Promise<Notification | null> => {
  return logActivity(
    ACTIVITY_CONFIGS.INCOME_CREATED(title, amount),
    source,
    authToken,
    { transactionId }
  )
}

export default {
  logActivity,
  logFieldActivity,
  logTaskActivity,
  logExpenseActivity,
  logIncomeActivity,
  ACTIVITY_CONFIGS,
}





